import React, { useEffect, useRef, useState } from 'react';
import {Typography, Grid, FormGroup, FormControlLabel, Switch, Box, Stack} from '@mui/material';
import {Input, Select, Option, FormControl, FormLabel} from '@mui/joy';
import moment from "moment";

import { countries_arr, regions_arr } from "../common/staticVariables";

import GeoLocationTrackingMap from '../components/geolocation-tracking/geolocation-tracking-map.component';
import RegionTrackingCountsComponent from '../components/geolocation-tracking/regions-tracking-counts.component';
import DefaultTabsComponent from "../components/common/default-tabs.component";
import VerificationAverageComponent from '../components/geolocation-tracking/verification-average.component';
import ScanningActivityComponent from '../components/geolocation-tracking/scanning-activity.component';
import DateRangePickerComponent from '../components/common/CustomDateRangePicker';
import useCommonOvcodeUnilab from '../hooks/useCommonOvcodeUnilab';
import useGeolocation from '../hooks/useGeolocation';

import ReactSelect from "react-select";

//TODO
// 4. Call get lat lng details using date range and region filters as default for first init
// 5. Calculate center using array of lat lng for init map
// 6. Create json formatted for mapbox API
// 7. Add markers and popups and display map
// 8. Implement product filter
// 9. Implement location filter

const filterStyles = {
    control: (styles, { isFocused }) => ({
        ...styles,
        textAlign: "left",
        backgroundColor: "#fff",
        minHeight: 20, 
        padding: "0 8px", 
        height: 34, 
        width: 200,
        fontSize: 14,
        borderColor: "rgb(122, 120, 247)",
        '&:hover': {
            borderColor: "rgb(122, 120, 247)", 
        },
        zIndex: 1200,
    }),
    menu: (provided) => ({
        ...provided,
        zIndex: 1200,
    }),
    menuPortal: (provided) => ({
        ...provided,
        zIndex: 9999, 
    }),
    valueContainer: (styles) => ({
        ...styles,
        padding: "0px 6px",
    }),
    input: (styles) => ({
        ...styles,
        margin: 0,
        padding: 0, 
    }),
    option: (styles) => ({...styles, textAlign: "left", fontSize: 13}),
    dropdownIndicator: (styles) => ({
        ...styles,
        color: "rgb(122, 120, 247)", 
        marginTop: "-2px",
        '&:hover': {
            color: "rgb(122, 120, 247)",
        },
    }),
    clearIndicator: (styles) => ({
        ...styles,
        color: "rgb(122, 120, 247)", 
        marginTop: "-2px",
        '&:hover': {
            color: "rgb(122, 120, 247)", 
        },
    }),
    singleValue: (styles) => ({
        ...styles,
        color: "rgba(51, 51, 207, 0.808)",
        marginTop: "0px",
        fontSize: 13,
    }),
    placeholder: (styles) => ({ ...styles, color: "#3333CFCE", marginTop: "0px", fontSize: 12}), 
    noOptionsMessage: (provided) => ({
        ...provided,
        fontStyle: "italic", 
        textAlign: "start", 
        fontSize: "13px", 
    }),
}

function DebounceInput(props) {
    const { setValue, setShowInputLoading, setShowInputClearBtn, handleDebounce, debounceTimeout, value, ...rest } = props;
  
    const timerRef = React.useRef();

    const handleChange = (event) => {
        setValue(event.target.value)
        setShowInputLoading(!!event.target.value)
        setShowInputClearBtn(!!event.target.value)
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            handleDebounce(event.target.value);
            setShowInputClearBtn(!!event.target.value)
        }, debounceTimeout);
    };
  
    return <Input {...rest} value={value} onChange={handleChange} />;
}

const initialStartDate = moment().subtract(6, 'days').format('YYYY-MM-DD')
const initialEndDate = moment().format('YYYY-MM-DD')

const useGeolocationTrackingTabs = (regions, scanningActivityData) => {

    const [geolocationTrackingTabs, setGeolocationTrackingTabs] = useState([]);

    function setupTabs(regions, scanningActivityData) {
        const geolocation_tracking_tabs = [
            {
                label: "Scanning Activity",
                component: <ScanningActivityComponent 
                    regions={regions} 
                    scanningActivityData={scanningActivityData} 
                />
            },
            {
                label: "New Verification Average",
                component: <VerificationAverageComponent regions={regions} />
            },
        ]
        setGeolocationTrackingTabs(geolocation_tracking_tabs);
    }

    useEffect(() => { 
        setupTabs(regions, scanningActivityData) 
    },[JSON.stringify(regions), JSON.stringify(scanningActivityData)]);

    return {geolocationTrackingTabs}
}

const GeolocationTrackingScreen = () => {

    const regionsContainerRef = useRef();

    const [countries, setCountries] = useState(countries_arr);
    const [currentCountry, setCurrentCountry] = useState(countries_arr[0]);
    const [currentProvince, setCurrentProvince] = useState(null);
    const [currentProduct, setCurrentProduct] = useState(null);
    const [regions, setRegions] = useState(regions_arr);
    const [currentRegion, setCurrentRegion] = useState({});
    const [startDate, setStartDate] = useState(initialStartDate);
    const [endDate, setEndDate] = useState(initialEndDate);
    const [viewHeatmap, setViewHeatmap] = useState(false);
    const [verificationData, setVerificationData] = useState([]);
    const [geolocationData, setGeolocationData] = useState([]);

    const {distinctProvinceData, getDistinctProvince, distinctProductsData, getDistinctProducts} = useCommonOvcodeUnilab()
    const {verificationCountData, getVerificationCount, verificationLatLngData, getVerificationLatLng, scanningActivityData, 
        getScanningActivity, isLoadingGetVerificationCount, isLoadingGetScanningActivity
    } = useGeolocation();

    const { geolocationTrackingTabs } = useGeolocationTrackingTabs(regions, scanningActivityData, isLoadingGetVerificationCount, isLoadingGetScanningActivity)

    useEffect(() => {
        let filters = {
            country: currentCountry,
            startDate,
            endDate,
            region: currentRegion?.region,
            province: currentProvince?.value,
            product_code: currentProduct?.value,
        }

        getVerificationCount(filters)
        getVerificationLatLng(filters)
        getScanningActivity(filters)
    }, [startDate, endDate, currentCountry, JSON.stringify(currentRegion), JSON.stringify(currentProvince), JSON.stringify(currentProduct)])

    useEffect(() => {
        if (currentRegion?.region) {
            getDistinctProvince(currentCountry, currentRegion?.region)
        }
    }, [JSON.stringify(currentRegion)])

    useEffect(() => {
        getDistinctProducts()
    }, [])

    useEffect(() => {
        // Calculate the total count
        const total = verificationCountData.reduce((sum, item) => sum + item.count, 0);

        // Map the data to include the percentage
        setRegions((prev) => {
            const udpated_regions = prev.map(item => {
                let region = verificationCountData.find((count_item) => count_item.region == item.region)
                if (region) {
                    item.count = region.count
                    item.percent = parseFloat(((region.count / total) * 100).toFixed(2))
                } else {
                    item.count = 0
                    item.percent = 0
                }
                return item
            });
            return udpated_regions
        })
    }, [JSON.stringify(verificationCountData)])

    useEffect(() => {
        setVerificationData(verificationLatLngData.length > 0 ? verificationLatLngData[0].locations : [])
        setGeolocationData(verificationLatLngData.length > 0 ? verificationLatLngData : []);
    }, [JSON.stringify(verificationLatLngData)])

    const onChangeRegion = (value) => {
        setCurrentRegion(value);
        setCurrentProvince(null);
    }

    // const handleDebounceLocation = (value) => {
    //     setSearchLocation(value)
    //     setShowInputLocationLoading(false)
    //     setShowInputLocationClearBtn(!showInputLocationLoading && value != "")
    // };

    // const handleDebounceProduct = (value) => {
    //     setSearchProduct(value)
    //     setShowInputProductLoading(false)
    //     setShowInputProductClearBtn(!showInputProductLoading && value != "")
    // };

    // const handleClearSearchLocation = () => {
    //     setInputLocationValue("")
    //     setSearchLocation(null)
    //     setShowInputLocationClearBtn(false)
    // }

    // const handleClearSearchProduct = () => {
    //     setInputProductValue("")
    //     setSearchProduct(null)
    //     setShowInputProductClearBtn(false)
    // }

    const handleToggleHeatmap = (event) => {
        setViewHeatmap(event.target.checked)
    }

    const handleDateRangeChange = (startDate, endDate) => {
        setStartDate(startDate)
        setEndDate(endDate)
    }

    const handleSelectProductFilter = (selectedOption) => {
        if (!selectedOption) {
            setCurrentProduct("");

        } else {
            setCurrentProduct(selectedOption);
        }
    }

    const handleSelectProvinceFilter = (selectedOption) => {
        if (!selectedOption) {
            setCurrentProvince("");

        } else {
            setCurrentProvince(selectedOption);
        }
    }


    return(
        <>
            <Stack spacing={1} direction="row">
                <Typography variant="h6" sx={{color: "#58565f", fontWeight: 600, fontSize: 20}}>
                    Geolocation Monitoring
                </Typography>
                <Box sx={{flexGrow: 1}} />
                <FormControl>
                    <FormLabel id="select-field-demo-label" htmlFor="select-field-demo-button">
                        Supported Country:
                    </FormLabel>
                    <Select defaultValue={currentCountry} placeholder="Supported Countries">
                        {countries.map((item) => <Option value={item}>{item}</Option>)}
                    </Select>
                </FormControl>
            </Stack>
            <br />
            <Grid container spacing={2}>
                <Grid item xs={12} sm={12} md={7} lg={7} xl={7}>
                    <Box sx={{display: 'flex', flexDirection: 'row'}}>
                        <FormGroup>
                            <FormControlLabel control={<Switch defaultChecked />} label="View Heatmap" checked={viewHeatmap} onChange={handleToggleHeatmap} />
                        </FormGroup>
                        <Box sx={{flexGrow: 1}} />
                        {/* <DebounceInput
                            placeholder="Enter location"
                            startDecorator={<Typography>Location:</Typography>}
                            setShowInputLoading={setShowInputLocationLoading} 
                            setShowInputClearBtn={setShowInputLocationClearBtn}
                            endDecorator={showInputLocationLoading && <CircularProgress size={20} /> || showInputLocationClearBtn && <CloseIcon onClick={handleClearSearchLocation} />} 
                            debounceTimeout={500}
                            value={inputLocationValue}
                            setValue={setInputLocationValue}
                            handleDebounce={handleDebounceLocation}
                        /> */}

                        <ReactSelect
                            isRtl={false}
                            options={distinctProvinceData?.map(province => ({
                                label: province.province,
                                value: province.province
                            }))}
                            value={currentProvince}
                            onChange={selectedOption => handleSelectProvinceFilter(selectedOption)}
                            styles={filterStyles}
                            isClearable={true}
                            placeholder={(currentProvince) ? null : "Select Province"}
                            noOptionsMessage={() => "No matching province found."} 
                            menuPortalTarget={document.body} 
                            menuPosition="fixed"
                        /> 
                    </Box>

                    <br />

                    <GeoLocationTrackingMap regionsContainerRef={regionsContainerRef} verificationData={verificationData} 
                        viewHeatmap={viewHeatmap} geolocationData={geolocationData} 
                    />
                </Grid>

                <Grid item xs={12} sm={12} md={5} lg={5} xl={5}>
                    <Stack spacing={1} direction="row">
                        <Grid>
                            <ReactSelect
                                isRtl={false}
                                options={distinctProductsData?.map(product => ({
                                    label: product.product_name,
                                    value: product.product_code
                                }))}
                                value={currentProduct}
                                onChange={selectedOption => handleSelectProductFilter(selectedOption)}
                                styles={filterStyles}
                                isClearable={true}
                                placeholder={(currentProduct) ? null : "Select Product Name"}
                                noOptionsMessage={() => "No matching product found."} 
                                menuPortalTarget={document.body} 
                                menuPosition="fixed"
                            /> 
                        </Grid>

                        <Grid sx={{marginLeft: "auto !important"}}>
                            <DateRangePickerComponent
                                initialStartDate={initialStartDate}
                                initialEndDate={initialEndDate}
                                ranges={{
                                    "Last 7 Days": [moment().subtract(6, 'days'), moment()],
                                    "This Month": [moment().startOf('month'), moment().endOf('month')],
                                    "Last Month": [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')],
                                    "Last 90 Days": [moment().subtract(89, 'days'), moment()],
                                }}
                                onDateRangeChange={handleDateRangeChange}
                            />
                        </Grid>
                    </Stack>

                    <br />

                    <RegionTrackingCountsComponent 
                        regionsContainerRef={regionsContainerRef} 
                        regions={regions} 
                        isLoadingGetVerificationCount={isLoadingGetVerificationCount} 
                        onChangeRegion={onChangeRegion}
                    />
                </Grid>
            </Grid>
            <br />
            
            <DefaultTabsComponent tabs={geolocationTrackingTabs} />
           
        </>
    );
}

export default GeolocationTrackingScreen